/**
 * Admin -> Identity QR verification (trust anchors)
 *
 * Where a customer pastes the trusted PUBLIC certificate used to
 * cryptographically verify signed identity QR codes. A valid signature is proof
 * a scanned card was issued by the authority and is untampered - the strongest
 * possible answer to "is this real", with zero false positives.
 *
 * "Ready to integrate": the day the UIDAI signing certificate is issued, paste
 * it here and Aadhaar QR verification goes live - no redeploy.
 *
 * The certificate is a public key (not a secret), so it is shown as a preview
 * and a bad paste is rejected on save.
 *
 * Backend: /v1/admin/qr-trust-anchor (GET/PUT/test/DELETE).
 */

import { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  BadgeCheck,
  CheckCircle2,
  FileSearch,
  Info,
  Loader2,
  RefreshCw,
  Save,
  ShieldCheck,
  Trash2,
} from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import {
  getQrTrustAnchors,
  upsertQrTrustAnchor,
  testQrTrustAnchor,
  clearQrTrustAnchor,
  type QrTrustAnchorState,
  type AadhaarQrVerifyResult,
} from '@/lib/api';

const INPUT =
  'glass-lite rounded-md px-3 py-2 text-sm w-full outline-none focus:border-primary/40 border border-border/40';

const UIDAI_KIND = 'uidai_aadhaar_qr';

const SIG_COPY: Record<AadhaarQrVerifyResult['signature'], { label: string; tone: 'success' | 'warn' | 'danger' }> = {
  valid: { label: 'Signature valid - genuine UIDAI-issued card', tone: 'success' },
  invalid: { label: 'Signature INVALID - tampered or forged', tone: 'danger' },
  no_cert: { label: 'No certificate configured - parsed only, not verified', tone: 'warn' },
  unparseable: { label: 'Not a decodable Secure QR payload', tone: 'danger' },
};

export function AdminQrTrustAnchor(): JSX.Element {
  const [state, setState] = useState<QrTrustAnchorState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [certPem, setCertPem] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [qr, setQr] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<AadhaarQrVerifyResult | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await getQrTrustAnchors();
      setState(s);
      const uidai = s.anchors.find((a) => a.kind === UIDAI_KIND);
      if (uidai) setEnabled(uidai.enabled);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load QR trust anchors');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const uidaiAnchor = state?.anchors.find((a) => a.kind === UIDAI_KIND);
  const uidaiInfo = state?.catalogue.find((c) => c.kind === UIDAI_KIND);

  async function onSave() {
    if (!certPem.trim()) {
      setError('Paste the UIDAI public certificate (PEM) first.');
      return;
    }
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await upsertQrTrustAnchor({ kind: UIDAI_KIND, certPem: certPem.trim(), enabled });
      setCertPem('');
      setSaved(true);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function onClear() {
    setSaving(true);
    try {
      await clearQrTrustAnchor(UIDAI_KIND);
      setCertPem('');
      setEnabled(true);
      setTestResult(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Clear failed');
    } finally {
      setSaving(false);
    }
  }

  async function onTest() {
    if (!/^\d+$/.test(qr.trim())) {
      setTestResult({ parsed: false, signature: 'unparseable', error: 'Paste the QR payload (the big number the QR encodes).' });
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      setTestResult(await testQrTrustAnchor(qr.trim()));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Test failed');
    } finally {
      setTesting(false);
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title="Identity QR verification"
        subtitle="Cryptographically verify signed identity QR codes against the issuing authority's certificate."
        badges={[
          {
            label: uidaiAnchor?.enabled && uidaiAnchor.hasCert ? 'UIDAI cert active' : 'UIDAI cert not set',
            variant: uidaiAnchor?.enabled && uidaiAnchor.hasCert ? 'success' : 'glass',
          },
        ]}
        actions={
          <Button size="sm" variant="glass" onClick={() => void load()}>
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
        }
      />

      {loading && <LoadingState caption="Loading trust anchors..." />}
      {error && !loading && (
        <ErrorState title="QR verification" detail={error} onRetry={() => void load()} />
      )}

      {!loading && state && (
        <>
          <GlassCard className="p-4 mb-4">
            <CardHeader className="p-0 mb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-primary" /> How this works
              </CardTitle>
              <CardDescription clamp className="text-xs">
                An Aadhaar Secure QR is digitally signed by UIDAI. With UIDAI's public certificate
                loaded here, a scanned card's signature can be verified offline - a valid signature
                is cryptographic proof the card is genuine and untampered, with no false positives
                and no per-check API cost. Verification is already built; it activates the moment the
                certificate is present.
              </CardDescription>
            </CardHeader>
          </GlassCard>

          {/* UIDAI certificate */}
          <GlassCard className="p-5 space-y-4 mb-4">
            <CardTitle className="text-sm flex items-center gap-2">
              <BadgeCheck className="h-4 w-4 text-primary" /> {uidaiInfo?.label ?? 'UIDAI Aadhaar Secure QR'}
              {uidaiAnchor?.hasCert && (
                <Badge variant="success" className="text-[9px]">
                  Loaded
                </Badge>
              )}
            </CardTitle>
            <div className="text-xs text-muted-foreground">{uidaiInfo?.help}</div>

            {uidaiAnchor?.certPreview && (
              <div className="glass-lite rounded-md p-2 text-[11px] font-mono text-muted-foreground break-all">
                {uidaiAnchor.certPreview}
              </div>
            )}

            <div>
              <label htmlFor="qr-cert" className="text-xs font-medium block mb-1">
                UIDAI public certificate (PEM)
              </label>
              <textarea
                id="qr-cert"
                className={`${INPUT} font-mono text-[11px] min-h-[140px]`}
                value={certPem}
                onChange={(e) => setCertPem(e.target.value)}
                placeholder={
                  uidaiAnchor?.hasCert
                    ? 'A certificate is loaded. Paste a new one to replace it.'
                    : '-----BEGIN CERTIFICATE-----\n... paste UIDAI\'s public signing certificate ...\n-----END CERTIFICATE-----'
                }
                spellCheck={false}
              />
              <div className="text-[10px] text-muted-foreground mt-1">
                Public certificate - not a secret. A value that does not parse as a certificate is
                rejected on save, so it can never silently fail to verify.
              </div>
            </div>

            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              Enabled (verification will use this certificate)
            </label>

            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Button size="sm" onClick={() => void onSave()} disabled={saving} data-action="disc.adm_qr.a01">
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
                certificate
              </Button>
              {uidaiAnchor && (
                <Button size="sm" variant="glass" onClick={() => void onClear()} disabled={saving} data-action="disc.adm_qr.a02">
                  <Trash2 className="h-3 w-3" /> Remove
                </Button>
              )}
              {saved && (
                <span className="text-[11px] text-success flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" /> Saved
                </span>
              )}
            </div>
          </GlassCard>

          {/* Live test */}
          <GlassCard className="p-5 space-y-3 mb-4">
            <CardTitle className="text-sm flex items-center gap-2">
              <FileSearch className="h-4 w-4 text-primary" /> Test a scanned QR
            </CardTitle>
            <div className="text-xs text-muted-foreground">
              Paste the payload a QR scanner reads from an Aadhaar Secure QR (a long number). It is
              verified against the certificate above - useful to confirm the setup end-to-end.
            </div>
            <textarea
              className={`${INPUT} font-mono text-[11px] min-h-[80px]`}
              value={qr}
              onChange={(e) => setQr(e.target.value)}
              placeholder="Paste the Secure QR payload (digits)"
              spellCheck={false}
            />
            <Button size="sm" onClick={() => void onTest()} disabled={testing} data-action="disc.adm_qr.a03">
              {testing ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileSearch className="h-3 w-3" />}{' '}
              Verify
            </Button>
            {testResult && <QrVerdict result={testResult} />}
          </GlassCard>

          {/* DigiLocker - honest "different mechanism" note */}
          <GlassCard className="p-4">
            <CardTitle className="text-sm flex items-center gap-2">
              <Info className="h-4 w-4 text-muted-foreground" /> DigiLocker &amp; other providers
            </CardTitle>
            <CardDescription clamp className="text-xs mt-1">
              DigiLocker verification is a different mechanism - an authenticated document-fetch API
              rather than a QR certificate - so it will arrive as its own connector, not another
              certificate here. This surface is provider-scoped and ready to add it.
            </CardDescription>
          </GlassCard>
        </>
      )}
    </PageContainer>
  );
}

function QrVerdict({ result }: { result: AadhaarQrVerifyResult }): JSX.Element {
  const copy = SIG_COPY[result.signature];
  const border =
    copy.tone === 'success'
      ? 'border-success/40'
      : copy.tone === 'warn'
        ? 'border-amber-400/40'
        : 'border-destructive/40';
  return (
    <div className={`glass-lite rounded-md p-3 text-xs border ${border}`}>
      <div className="flex items-center gap-2 font-medium">
        {copy.tone === 'success' ? (
          <CheckCircle2 className="h-4 w-4 text-success" />
        ) : (
          <AlertCircle className={`h-4 w-4 ${copy.tone === 'warn' ? 'text-amber-400' : 'text-destructive'}`} />
        )}
        {copy.label}
      </div>
      {result.fields && (result.fields.name || result.fields.lastFourDigits) && (
        <div className="text-muted-foreground mt-1">
          {result.fields.name && <>Name: {result.fields.name} </>}
          {result.fields.lastFourDigits && <>· Aadhaar ****{result.fields.lastFourDigits} </>}
          {result.fields.gender && <>· {result.fields.gender}</>}
        </div>
      )}
      {result.error && <div className="text-destructive mt-1">{result.error}</div>}
    </div>
  );
}
